import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface DataHealthCsvRow {
  namc: string;
  shop: string;
  eventRb: number | null;
  toolRb: number | null;
  trendRb: number | null;
  cycleRb: number | null;
  summaryRb: number | null;
  qualityCount: number | null;
  qualityFaultCount: number | null;
  qualityPct: number | null;
  healthCount: number | null;
  healthFaultCount: number | null;
  healthPct: number | null;
}

interface DataHealthNamcRow {
  namc: string;
  shopRows: DataHealthCsvRow[];
  eventRb: number;
  toolRb: number;
  trendRb: number;
  cycleRb: number;
  summaryRb: number;
  qualityCount: number;
  qualityFaultCount: number;
  qualityPct: number;
  healthCount: number;
  healthFaultCount: number;
  healthPct: number;
  status: 'Good' | 'Watch' | 'Critical' | 'Verify' | 'No Data';
}

interface DataHealthDistributionItem {
  label: string;
  count: number;
  color: string;
}

interface RingBufferMetricRow {
  key: 'Event RB' | 'Tool RB' | 'Trend RB' | 'Cycle RB' | 'Summary RB';
  count: number;
  health: 'Good' | 'Watch' | 'Critical';
}

@Component({
  selector: 'app-deployment-data-health',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './deployment-data-health.component.html',
  styleUrl: './deployment-data-health.component.scss'
})
export class DeploymentDataHealthComponent implements OnInit {

  // ── Data Health CSV state ───────────────────────────────────────────────
  dataHealthLoading = true;
  dataHealthError = '';
  dataHealthRows: DataHealthCsvRow[] = [];
  dataHealthNamcRows: DataHealthNamcRow[] = [];
  dataHealthExpandedNamcs: Record<string, boolean> = {};
  dataHealthSearchTerm = '';

  ngOnInit(): void {
    void this.loadDataHealthCsv();
  }

  // ── Data Health computed state ──────────────────────────────────────────

  get dataHealthTotalShops(): number {
    return this.dataHealthRows.length;
  }

  get dataHealthTotalHealthCount(): number {
    return this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.healthCount), 0);
  }

  get dataHealthTotalHealthFaultCount(): number {
    return this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.healthFaultCount), 0);
  }

  get dataHealthTotalQualityCount(): number {
    return this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.qualityCount), 0);
  }

  get dataHealthTotalQualityFaultCount(): number {
    return this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.qualityFaultCount), 0);
  }

  get dataHealthWeightedHealthPct(): number {
    return this.calculateRatioPct(this.dataHealthTotalHealthCount, this.dataHealthTotalHealthFaultCount);
  }

  get dataHealthWeightedQualityPct(): number {
    return this.calculateRatioPct(this.dataHealthTotalQualityCount, this.dataHealthTotalQualityFaultCount);
  }

  get dataHealthShopsToVerifyCount(): number {
    return this.dataHealthRows.filter(row => this.isVerifyHealthRow(row)).length;
  }

  get dataHealthDistributionItems(): DataHealthDistributionItem[] {
    const counters = {
      acceptable: 0,
      watch: 0,
      critical: 0,
      verify: 0,
      noData: 0
    };

    this.dataHealthRows.forEach(row => {
      const category = this.categorizeShopHealth(row);
      if (category === 'Acceptable') { counters.acceptable += 1; }
      else if (category === 'Watch') { counters.watch += 1; }
      else if (category === 'Critical') { counters.critical += 1; }
      else if (category === 'Verify') { counters.verify += 1; }
      else { counters.noData += 1; }
    });

    return [
      { label: 'Acceptable (>= 85%)', count: counters.acceptable, color: '#24a86b' },
      { label: 'Watch (70% - 84%)', count: counters.watch, color: '#f39c12' },
      { label: 'Critical (< 70%)', count: counters.critical, color: '#ef4444' },
      { label: 'Verify (100%)', count: counters.verify, color: '#8b5cf6' },
      { label: 'No Data (0%)', count: counters.noData, color: '#cbd5e1' }
    ];
  }

  get dataHealthDonutSegments(): string {
    const items = this.dataHealthDistributionItems;
    const total = items.reduce((sum, item) => sum + item.count, 0);
    if (total === 0) {
      return 'conic-gradient(#e2e8f0 0 100%)';
    }

    let cumulative = 0;
    const segments = items
      .filter(item => item.count > 0)
      .map(item => {
        const start = (cumulative / total) * 100;
        cumulative += item.count;
        const end = (cumulative / total) * 100;
        return `${item.color} ${start}% ${end}%`;
      });

    return `conic-gradient(${segments.join(', ')})`;
  }

  get dataHealthRingBufferRows(): RingBufferMetricRow[] {
    const eventRb = this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.eventRb), 0);
    const toolRb = this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.toolRb), 0);
    const trendRb = this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.trendRb), 0);
    const cycleRb = this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.cycleRb), 0);
    const summaryRb = this.dataHealthRows.reduce((sum, row) => sum + this.numberOrZero(row.summaryRb), 0);

    return [
      { key: 'Event RB', count: eventRb, health: this.ringBufferHealth(eventRb, 'Event RB') },
      { key: 'Tool RB', count: toolRb, health: this.ringBufferHealth(toolRb, 'Tool RB') },
      { key: 'Trend RB', count: trendRb, health: this.ringBufferHealth(trendRb, 'Trend RB') },
      { key: 'Cycle RB', count: cycleRb, health: this.ringBufferHealth(cycleRb, 'Cycle RB') },
      { key: 'Summary RB', count: summaryRb, health: this.ringBufferHealth(summaryRb, 'Summary RB') }
    ];
  }

  get dataHealthByNamcRows(): DataHealthNamcRow[] {
    return [...this.dataHealthNamcRows].sort((a, b) => b.healthPct - a.healthPct);
  }

  get qualityByNamcRows(): DataHealthNamcRow[] {
    return [...this.dataHealthNamcRows].sort((a, b) => b.qualityPct - a.qualityPct);
  }

  get filteredDataHealthNamcRows(): DataHealthNamcRow[] {
    const term = this.dataHealthSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.dataHealthByNamcRows;
    }

    return this.dataHealthByNamcRows.filter(row =>
      row.namc.toLowerCase().includes(term) ||
      row.shopRows.some(shop => shop.shop.toLowerCase().includes(term))
    );
  }

  get maxHealthPctAcrossNamc(): number {
    const values = this.dataHealthByNamcRows.map(row => row.healthPct);
    return values.length > 0 ? Math.max(...values) : 100;
  }

  get maxQualityPctAcrossNamc(): number {
    const values = this.qualityByNamcRows.map(row => row.qualityPct);
    return values.length > 0 ? Math.max(...values) : 100;
  }

  percentageOfTotalShops(count: number): number {
    if (this.dataHealthTotalShops === 0) {
      return 0;
    }
    return Math.round((count / this.dataHealthTotalShops) * 1000) / 10;
  }

  dataHealthStatusClass(status: DataHealthNamcRow['status']): string {
    if (status === 'Good') { return 'dh-status-good'; }
    if (status === 'Watch') { return 'dh-status-watch'; }
    if (status === 'Critical') { return 'dh-status-critical'; }
    if (status === 'Verify') { return 'dh-status-verify'; }
    return 'dh-status-na';
  }

  dataHealthShopStatusLabel(row: DataHealthCsvRow): 'Good' | 'Watch' | 'Critical' | 'Verify' | 'No Data' {
    const category = this.categorizeShopHealth(row);
    if (category === 'Acceptable') {
      return 'Good';
    }
    return category;
  }

  dataHealthShopStatusClass(row: DataHealthCsvRow): string {
    const status = this.dataHealthShopStatusLabel(row);
    return this.dataHealthStatusClass(status);
  }

  ringBufferStatusClass(status: RingBufferMetricRow['health']): string {
    if (status === 'Good') { return 'dh-pill-good'; }
    if (status === 'Watch') { return 'dh-pill-watch'; }
    return 'dh-pill-critical';
  }

  toggleDataHealthNamc(namc: string): void {
    this.dataHealthExpandedNamcs[namc] = !this.dataHealthExpandedNamcs[namc];
  }

  isDataHealthNamcExpanded(namc: string): boolean {
    return !!this.dataHealthExpandedNamcs[namc];
  }

  expandAllDataHealthNamcs(): void {
    this.dataHealthNamcRows.forEach(row => {
      this.dataHealthExpandedNamcs[row.namc] = true;
    });
  }

  collapseAllDataHealthNamcs(): void {
    this.dataHealthNamcRows.forEach(row => {
      this.dataHealthExpandedNamcs[row.namc] = false;
    });
  }

  trackByDataHealthNamc(_: number, row: DataHealthNamcRow): string {
    return row.namc;
  }

  trackByDataHealthShop(_: number, row: DataHealthCsvRow): string {
    return `${row.namc}_${row.shop}`;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private async loadDataHealthCsv(): Promise<void> {
    this.dataHealthLoading = true;
    this.dataHealthError = '';
    try {
      const response = await fetch('assets/data/cleansing-data-20260729-112425.csv', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Unable to load CSV (${response.status})`);
      }

      const csvText = await response.text();
      this.dataHealthRows = this.parseDataHealthCsv(csvText);
      this.buildDataHealthNamcRows();
    }
    catch (error) {
      this.dataHealthError = error instanceof Error ? error.message : 'Failed to load data health CSV';
      this.dataHealthRows = [];
      this.dataHealthNamcRows = [];
      console.info('Unable to load data health CSV', error);
    }
    finally {
      this.dataHealthLoading = false;
    }
  }

  private parseDataHealthCsv(csvText: string): DataHealthCsvRow[] {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length <= 1) {
      return [];
    }

    const rows: DataHealthCsvRow[] = [];
    lines.slice(1).forEach(line => {
      const cols = line.split(',');
      const shopToken = (cols[0] ?? '').trim();
      if (!shopToken) {
        return;
      }

      const [namc, ...shopParts] = shopToken.split('_');
      rows.push({
        namc: namc?.trim() ?? '',
        shop: shopParts.join('_').trim() || shopToken,
        eventRb: this.toNullableNumber(cols[1]),
        toolRb: this.toNullableNumber(cols[2]),
        trendRb: this.toNullableNumber(cols[3]),
        cycleRb: this.toNullableNumber(cols[4]),
        summaryRb: this.toNullableNumber(cols[5]),
        qualityCount: this.toNullableNumber(cols[6]),
        qualityFaultCount: this.toNullableNumber(cols[7]),
        qualityPct: this.toNullableNumber(cols[8]),
        healthCount: this.toNullableNumber(cols[9]),
        healthFaultCount: this.toNullableNumber(cols[10]),
        healthPct: this.toNullableNumber(cols[11])
      });
    });

    return rows;
  }

  private buildDataHealthNamcRows(): void {
    const grouped = new Map<string, DataHealthCsvRow[]>();
    this.dataHealthRows.forEach(row => {
      const key = row.namc || 'UNKNOWN';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(row);
    });

    this.dataHealthNamcRows = Array.from(grouped.entries())
      .map(([namc, shopRows]) => {
        const healthCount = shopRows.reduce((sum, row) => sum + this.numberOrZero(row.healthCount), 0);
        const healthFaultCount = shopRows.reduce((sum, row) => sum + this.numberOrZero(row.healthFaultCount), 0);
        const qualityCount = shopRows.reduce((sum, row) => sum + this.numberOrZero(row.qualityCount), 0);
        const qualityFaultCount = shopRows.reduce((sum, row) => sum + this.numberOrZero(row.qualityFaultCount), 0);

        const healthPct = this.calculateRatioPct(healthCount, healthFaultCount);
        const qualityPct = this.calculateRatioPct(qualityCount, qualityFaultCount);
        const hasAnyData = shopRows.some(row => row.healthPct !== null || row.qualityPct !== null);

        return {
          namc,
          shopRows,
          eventRb: shopRows.reduce((sum, row) => sum + this.numberOrZero(row.eventRb), 0),
          toolRb: shopRows.reduce((sum, row) => sum + this.numberOrZero(row.toolRb), 0),
          trendRb: shopRows.reduce((sum, row) => sum + this.numberOrZero(row.trendRb), 0),
          cycleRb: shopRows.reduce((sum, row) => sum + this.numberOrZero(row.cycleRb), 0),
          summaryRb: shopRows.reduce((sum, row) => sum + this.numberOrZero(row.summaryRb), 0),
          qualityCount,
          qualityFaultCount,
          qualityPct,
          healthCount,
          healthFaultCount,
          healthPct,
          status: this.healthStatusFromPct(healthPct, healthCount, hasAnyData)
        };
      })
      .sort((a, b) => a.namc.localeCompare(b.namc));

    const freshExpanded: Record<string, boolean> = {};
    this.dataHealthNamcRows.forEach(row => {
      freshExpanded[row.namc] = row.namc === 'TMMI';
    });
    this.dataHealthExpandedNamcs = freshExpanded;
  }

  private toNullableNumber(value: string | undefined): number | null {
    if (!value || value.trim() === '') {
      return null;
    }
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  private numberOrZero(value: number | null): number {
    return value ?? 0;
  }

  private calculateRatioPct(count: number, faultCount: number): number {
    if (count <= 0) {
      return 0;
    }
    const pct = ((count - faultCount) / count) * 100;
    return Math.round(pct * 100) / 100;
  }

  private isVerifyHealthRow(row: DataHealthCsvRow): boolean {
    return row.healthCount === 0 || row.healthPct === 100;
  }

  private categorizeShopHealth(row: DataHealthCsvRow): 'Acceptable' | 'Watch' | 'Critical' | 'Verify' | 'No Data' {
    if (row.healthPct === null) {
      return 'No Data';
    }
    if (this.isVerifyHealthRow(row)) {
      return 'Verify';
    }
    if (row.healthPct >= 85) {
      return 'Acceptable';
    }
    if (row.healthPct >= 70) {
      return 'Watch';
    }
    return 'Critical';
  }

  private healthStatusFromPct(
    healthPct: number,
    healthCount: number,
    hasAnyData: boolean
  ): 'Good' | 'Watch' | 'Critical' | 'Verify' | 'No Data' {
    if (!hasAnyData) {
      return 'No Data';
    }
    if (healthCount === 0 || healthPct === 100) {
      return 'Verify';
    }
    if (healthPct >= 85) {
      return 'Good';
    }
    if (healthPct >= 70) {
      return 'Watch';
    }
    return 'Critical';
  }

  private ringBufferHealth(count: number, key: RingBufferMetricRow['key']): 'Good' | 'Watch' | 'Critical' {
    if (count <= 0) {
      return 'Critical';
    }
    if (key === 'Cycle RB') {
      return 'Watch';
    }
    return 'Good';
  }
}
